import { CSSProperties } from 'react';
import { F0, F1, TypedValue } from '@client/new/helpers/types';
import { Col, Row } from '@design-system/components/grid/grid';
import { IconButton } from '@design-system/components/button/iconButton';
import { useRowStyle } from './useRowStyle';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TextInput } from '@client/new/pages/promotions/view/components/TextInput';
import { Tooltip } from '@design-system/components/tooltip/tooltip';

export const FormRowHeader = (
  p: Pick<FormRowsProps, 'styleSchema'> & { style: CSSProperties },
) => (
  <Row style={p.style}>
    {p.styleSchema.map((cell, i) => (
      <Tooltip
        title={cell.tooltip}
        key={i}
        variant="dark"
        tooltipProps={{ width: 175 }}
      >
        <Col style={{ fontWeight: 'bold' }}>{cell.title}</Col>
      </Tooltip>
    ))}
  </Row>
);

export const CellRenderer = (p: Cell & { schema: StyleSchema }) => (
  <Col>
    {(() => {
      switch (p.type) {
        case 'text-input':
          return (
            <TextInput
              value={p.value.value}
              onChange={(e) => {
                console.log('onChange', e.target.value, p.value.onChange);
                p.value.onChange(e.target.value);
              }}
            />
          );
        case 'number-input':
          return (
            <input
              type="number"
              value={p.value.value}
              onChange={(e) => p.value.onChange(e.target.value)}
            />
          );
        case 'icon-button':
          return (
            <IconButton
              icon={p.value.icon}
              onClick={p.value.onClick}
              variant="text"
            />
          );
        default:
          return <span>{p.value}</span>;
      }
    })()}
  </Col>
);

type FormRowProps = Pick<FormRowsProps, 'styleSchema'> & {
  values: Array<Cell>;
  style: CSSProperties;
};

export const FormRow = (p: FormRowProps) => (
  <Row style={p.style}>
    {p.styleSchema.map((schema, i) => (
      <CellRenderer key={i} schema={schema} {...p.values[i]} />
    ))}
  </Row>
);

export type CellTextInput = TypedValue<
  'text-input',
  { onChange: F1<string>; value: string }
>;

export type CellNumberInput = TypedValue<
  'number-input',
  { onChange: F1<string>; min?: number; max?: number; value: number }
>;

export type CellLabel = TypedValue<'label', string>;
export type CellButton = TypedValue<
  'icon-button',
  { icon: IconProp; onClick: F0 }
>;

export type Cell = CellTextInput | CellNumberInput | CellLabel | CellButton;
export type Row = { values: Array<Cell>; id: string };

export type StyleSchema = {
  width?: string | number;
  title?: string;
  tooltip?: string;
};

export type FormRowsProps = {
  styleSchema: Array<StyleSchema>;
  values: Array<Row>;
};

export const FormRows = (p: FormRowsProps) => {
  const style = useRowStyle(p);

  return (
    <Col style={{ minWidth: 0 }}>
      <FormRowHeader styleSchema={p.styleSchema} style={style} />
      {p.values.map(({ values, id }) => (
        <FormRow
          styleSchema={p.styleSchema}
          values={values}
          key={id}
          style={style}
        />
      ))}
    </Col>
  );
};
